import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import classes from "./css/mobile-sidebar.module.scss";

const MobileSideNavbar = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});

  useEffect(() => {
    const handler = () => setIsVisible(true);
    window.addEventListener("open-mobile-sidebar", handler);
    return () => window.removeEventListener("open-mobile-sidebar", handler);
  }, []);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  const close = useCallback(() => {
    setIsVisible(false);
    setOpenDropdowns({});
  }, []);

  const toggleDropdown = (key) => {
    setOpenDropdowns((prev) => {
      const isOpening = !prev[key];
      if (!isOpening) return { ...prev, [key]: false };

      const isSub = String(key).startsWith("sub-");
      const newState = {};
      Object.keys(prev).forEach((k) => {
        const kIsSub = String(k).startsWith("sub-");
        if (kIsSub === isSub) {
          newState[k] = false;
        } else {
          newState[k] = prev[k];
        }
      });
      newState[key] = true;
      return newState;
    });
  };

  const navItems =
    (window.navLinksTopMobile && window.navLinksTopMobile.navBar) || [];

  return (
    <>
      <div
        className={`${classes.overlay} ${
          isVisible ? classes.overlayVisible : ""
        }`}
        onClick={close}
      />

      <div
        className={`${classes.sidebar} ${isVisible ? classes.sidebarOpen : ""}`}
      >
        <div className={classes.sidebarHeader}>
          <Link
            to={`${process.env.PUBLIC_URL}/`}
            className={classes.logoLink}
            onClick={close}
          >
            {window.logo.mobile_menubar_logo.map((data, i) => (
              <img
                key={i}
                src={`${process.env.PUBLIC_URL}` + data.image}
                alt={data.alt}
                width="140"
                height="52"
              />
            ))}
          </Link>
          <button
            className={classes.closeBtn}
            onClick={close}
            type="button"
            aria-label="Close menu"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                stroke="#000"
                strokeWidth="1.1"
                x1="1"
                y1="1"
                x2="13"
                y2="13"
              />
              <line
                stroke="#000"
                strokeWidth="1.1"
                x1="13"
                y1="1"
                x2="1"
                y2="13"
              />
            </svg>
          </button>
        </div>

        <nav className={classes.menuList}>
          {navItems.map((item, i) => {
            if (item.type === "dropdown") {
              const childItems = item.children || [];
              const isOpen = openDropdowns[i];

              return (
                <div key={i} className={classes.menuItem}>
                  <button
                    className={classes.menuItemBtn}
                    onClick={() => toggleDropdown(i)}
                  >
                    <span>{item.heading}</span>
                    <ChevronIcon isOpen={isOpen} />
                  </button>
                  {childItems.length > 0 && (
                    <div
                      className={`${classes.accordion} ${
                        isOpen ? classes.accordionOpen : ""
                      }`}
                    >
                      <div className={classes.accordionInner}>
                        {childItems.map((child, j) => (
                          <SubMenuItem
                            key={j}
                            item={child}
                            parentKey={`${i}-${j}`}
                            openDropdowns={openDropdowns}
                            toggleDropdown={toggleDropdown}
                            onNavigate={close}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            if (item.type === "anchor") {
              return (
                <div key={i} className={classes.menuItem}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.menuItemBtn}
                    onClick={close}
                  >
                    <span>{item.heading}</span>
                  </a>
                </div>
              );
            }

            return (
              <div key={i} className={classes.menuItem}>
                <Link
                  to={`${process.env.PUBLIC_URL}/` + item.link}
                  className={classes.menuItemBtn}
                  onClick={close}
                >
                  <span>{item.heading}</span>
                </Link>
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
};

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ""}`}
    width="12"
    height="7"
    viewBox="0 0 12 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 1L6 6L11 1" stroke="#000" strokeWidth="1.2" />
  </svg>
);

const SubMenuItem = ({
  item,
  parentKey,
  openDropdowns,
  toggleDropdown,
  onNavigate,
}) => {
  if (item.type === "link") {
    return (
      <Link
        to={`${process.env.PUBLIC_URL}/` + item.link}
        className={classes.submenuLink}
        onClick={onNavigate}
      >
        {item.name}
      </Link>
    );
  }

  if (item.type === "group") {
    const isOpen = openDropdowns[`sub-${parentKey}`];
    return (
      <div className={classes.submenuGroup}>
        <button
          className={classes.submenuGroupToggle}
          onClick={() => toggleDropdown(`sub-${parentKey}`)}
        >
          <span>{item.heading}</span>
          <ChevronIcon isOpen={isOpen} />
        </button>
        {item.children.length > 0 && (
          <div
            className={`${classes.accordion} ${
              isOpen ? classes.accordionOpen : ""
            }`}
          >
            <div className={classes.accordionInner}>
              {item.children.map((child, k) => (
                <Link
                  key={k}
                  to={`${process.env.PUBLIC_URL}/` + child.link}
                  className={classes.nestedLink}
                  onClick={onNavigate}
                >
                  {child.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default MobileSideNavbar;
